import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { MEETING_SCHEDULES_REGISTRY_STATUS_UPDATE } from "../../routes/routes";

export function meetingHasStarted(meetingDate, meetingTime) {
  const start = moment(`${meetingDate || ""} ${meetingTime || ""}`, ["DD/MM/YYYY hh:mm a", "DD/MM/YYYY HH:mm:ss"], true);
  return start.isValid() && moment().isSameOrAfter(start);
}

export function postAttendanceUpdate(scheduleId, fields, file) {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(key, value);
  });
  if (file) formData.append("attachment", file);
  return axios.post(MEETING_SCHEDULES_REGISTRY_STATUS_UPDATE + scheduleId, formData);
}

export function AttendanceFlagHeaders() {
  return (
    <>
      <th style={{ textAlign: "center" }}>Attended</th>
      <th style={{ textAlign: "center" }}>Substitute</th>
      <th style={{ textAlign: "center" }}>Medical</th>
      <th style={{ textAlign: "center" }}>Attachment</th>
    </>
  );
}

function isImageFile(fileName) {
  return /\.(jpg|jpeg|png|webp|gif)$/i.test(String(fileName || ""));
}

function MedicalDocumentUpload({ onFile, label, icon, className }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    maxFiles: 1,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
      "image/gif": [".gif"],
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) onFile(acceptedFiles[0]);
    },
    onDropRejected: () => {
      toast.error("Only PDF and image files are allowed");
    },
  });

  return (
    <button type="button" {...getRootProps({ className })}>
      <input {...getInputProps()} />
      <i className={`bx ${icon}`}></i>
      <span className="ms-1">{isDragActive ? "Drop" : label}</span>
    </button>
  );
}

export function AttendanceFlagCells({ row, onDone }) {
  const [substituteForm, setSubstituteForm] = useState(null);

  const submit = (fields, file) => {
    postAttendanceUpdate(row.id, fields, file)
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          toast.success(response.data.msg);
          if (onDone) onDone();
        } else {
          toast.error(response.data.msg || "Failed to update attendance");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.msg || "Something went wrong");
        if (onDone) onDone();
      });
  };

  const attachmentUrl = row.attachment && typeof row.attachment === "object" ? row.attachment.url : "";
  const started = meetingHasStarted(row.meeting_date, row.meeting_time);
  const lockedTitle = started ? undefined : "Attendance can be marked after the meeting starts";

  const saveSubstitute = () => {
    const name = String(substituteForm?.name || "").trim();
    const mobile = String(substituteForm?.mobile || "").trim();
    if (!name) {
      toast.error("Substitute name is required");
      return;
    }
    if (!/^[0-9]{10}$/.test(mobile)) {
      toast.error("Substitute mobile number must be 10 digits");
      return;
    }
    submit({ is_substitute: 1, substitute_name: name, substitute_mobile: mobile });
    setSubstituteForm(null);
  };

  return (
    <>
      <td style={{ textAlign: "center" }}>
        <Form.Check
          type="switch"
          id={`att-${row.id}`}
          checked={row.meeting_status === 1}
          label=""
          disabled={!started}
          title={lockedTitle}
          onChange={(event) => submit({ status: event.target.checked ? 1 : 2 })}
        />
      </td>
      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <Form.Check
            type="switch"
            id={`sub-${row.id}`}
            className="m-0"
            checked={row.is_substitute === 1}
            label=""
            disabled={!started}
            title={lockedTitle}
            onChange={(event) => {
              if (event.target.checked) {
                setSubstituteForm({
                  name: row.substitute_name || "",
                  mobile: row.substitute_mobile || "",
                });
              } else {
                submit({ is_substitute: 0 });
              }
            }}
          />
          {row.is_substitute === 1 && (
            <button
              type="button"
              className="badge bg-label-warning border-0"
              title={started ? "Edit substitute" : lockedTitle}
              disabled={!started}
              onClick={() => setSubstituteForm({ name: row.substitute_name || "", mobile: row.substitute_mobile || "" })}
            >
              <i className="bx bx-edit-alt"></i>
            </button>
          )}
        </div>
      </td>
      <td style={{ textAlign: "center" }}>
        <Form.Check
          type="switch"
          id={`med-${row.id}`}
          checked={row.is_medical_leave === 1}
          label=""
          disabled={!started}
          title={lockedTitle}
          onChange={(event) => submit({ is_medical_leave: event.target.checked ? 1 : 0 })}
        />
      </td>
      <td style={{ textAlign: "center", verticalAlign: "middle" }}>
        {row.is_medical_leave === 1 ? (
          attachmentUrl ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              {isImageFile(row.attachment.file_name) ? (
                <a href={attachmentUrl} target="_blank" rel="noreferrer" title={row.attachment.file_name}>
                  <img
                    src={attachmentUrl}
                    alt={row.attachment.file_name}
                    style={{ width: 48, height: 36, objectFit: "cover", borderRadius: 6, display: "block" }}
                  />
                </a>
              ) : (
                <a
                  href={attachmentUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={row.attachment.file_name}
                  className="badge bg-label-danger text-decoration-none"
                >
                  <i className="bx bxs-file-pdf"></i>
                </a>
              )}
              <a
                href={attachmentUrl}
                target="_blank"
                rel="noreferrer"
                className="badge bg-label-info border-0 text-decoration-none"
                title="View"
              >
                <i className="bx bx-show"></i>
                <span className="ms-1">View</span>
              </a>
              <MedicalDocumentUpload
                label="Replace"
                icon="bx-edit-alt"
                className="badge bg-label-warning border-0"
                onFile={(file) => submit({}, file)}
              />
              <button
                type="button"
                className="badge bg-label-danger border-0"
                title="Delete"
                onClick={() => submit({ remove_attachment: 1 })}
              >
                <i className="bx bx-trash"></i>
              </button>
            </div>
          ) : (
            <MedicalDocumentUpload
              label="Upload"
              icon="bx-upload"
              className="badge bg-label-primary border-0"
              onFile={(file) => submit({}, file)}
            />
          )
        ) : (
          "-"
        )}
      </td>
      <Modal show={Boolean(substituteForm)} centered onHide={() => setSubstituteForm(null)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="modal_title1">Substitute details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Substitute name</label>
            <input
              className="form-control"
              value={substituteForm?.name || ""}
              onChange={(event) => setSubstituteForm({ ...substituteForm, name: event.target.value })}
            />
          </div>
          <div className="mb-1">
            <label className="form-label">Substitute mobile number</label>
            <input
              className="form-control"
              inputMode="numeric"
              maxLength={10}
              value={substituteForm?.mobile || ""}
              onChange={(event) => setSubstituteForm({ ...substituteForm, mobile: event.target.value.replace(/\D/g, "").slice(0, 10) })}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-label-secondary" onClick={() => setSubstituteForm(null)}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={saveSubstitute}>Save</button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
